import { Suspense, useEffect } from "react";
import { shallowEqual, useSelector } from "react-redux";

import { useCustomerServiceApi } from "@/api/hooks/customerService.hook";
import { SubscribeCustomerToBonusCardRequest } from "@/api/hooks/customerService.types";
import { BonusCardRequest } from "@/components/Account/BonusCardRequest/bonusCardRequest.component";
import { useThunkDispatch } from "@/redux/dispatcher";
import { load as loadAddressbook } from "@/redux/modules/api/data/addressbook/addressbook.module";
import { getAddresses } from "@/redux/modules/api/data/addressbook/addressbook.selectors";
import * as CartSelectors from "@/redux/modules/api/data/cart/cart.selectors";
import { getCart } from "@/redux/modules/api/data/cart/cart.selectors";
import * as CustomerModule from "@/redux/modules/api/data/customer/customer.module";
import * as CustomerSelectors from "@/redux/modules/api/data/customer/customer.selectors";
import { load as loadShopNames } from "@/redux/modules/api/data/stores/shopNames/shopNames.module";
import * as ShopNamesSelectors from "@/redux/modules/api/data/stores/shopNames/shopNames.selector";
import { getShopNames } from "@/redux/modules/api/data/stores/shopNames/shopNames.selector";
import { broadcastMessage } from "@/redux/modules/value/messages/messages.utils";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { setBonusCardRequestedState } from "@/services/bonusCard.service";
import { getPaymentAddress } from "@/utils/address/address";
import { BonusCardStatus } from "@/utils/enums";

type Props = {
    innerHTML?: string;
};

const FORM_ID = "bonusCardRequestForm";

const BonusCardRequestContainer = ({ innerHTML }: Props) => {
    const { t } = useGranite();
    const dispatch = useThunkDispatch();
    const isLoadingCustomer = useSelector(CustomerSelectors.isLoading);
    const isLoadingShopNames = useSelector(ShopNamesSelectors.isLoading);
    const customer = useSelector(CustomerSelectors.getCustomer);
    const cart = useSelector(CartSelectors.getCart);
    const bonusCardState = useSelector(CustomerSelectors.getBonusCardStatus);
    const shoppingCart = useSelector(getCart);
    const addresses = useSelector(getAddresses, shallowEqual);
    const shopNames = useSelector(getShopNames, shallowEqual);

    const [{ loading }, subscribeCustomerToBonusCard] = useCustomerServiceApi(
        {
            url: "/customers/bonuscard",
            method: "POST",
            withCredentials: true
        },
        { manual: true, useCache: false }
    );

    useEffect(() => {
        dispatch(CustomerModule.load());
        dispatch(loadAddressbook());
        if (shopNames.length > 0) return;
        dispatch(loadShopNames());
    }, [dispatch, shopNames]);

    const onSubmit = (
        request: SubscribeCustomerToBonusCardRequest
    ): Promise<void> => {
        if (
            [
                BonusCardStatus.CardOnRoute,
                BonusCardStatus.HasCard,
                BonusCardStatus.Requested
            ].some((status) => status === bonusCardState)
        ) {
            broadcastMessage({
                id: "bonuscard-subscribe-failure",
                text: t("bonuscardsubscribe.failure.hasbonuscard"),
                status: "danger",
                dismissible: true,
                activeScroll: true
            });
            return Promise.reject();
        }

        return new Promise((resolve, reject) => {
            subscribeCustomerToBonusCard({ data: request }).then(
                (response: { status: number }) => {
                    if (response.status >= 200 && response.status < 300) {
                        setBonusCardRequestedState(request.email);
                        broadcastMessage({
                            id: "bonuscard-subscribe-success",
                            text: t("bonuscardsubscribe.success.message"),
                            status: "success",
                            dismissible: true,
                            activeScroll: true
                        });
                        resolve(undefined);
                    } else {
                        broadcastMessage({
                            id: "bonuscard-subscribe-failure",
                            text: t("bonuscardsubscribe.failure.message"),
                            status: "danger",
                            dismissible: true,
                            activeScroll: true
                        });
                        reject();
                    }
                },
                () => {
                    broadcastMessage({
                        id: "bonuscard-subscribe-failure",
                        text: t("bonuscardsubscribe.failure.message"),
                        status: "danger",
                        dismissible: true,
                        activeScroll: true
                    });
                    reject();
                }
            );
        });
    };

    const getAddress = () => {
        return getPaymentAddress(addresses, shoppingCart);
    };

    return (
        <Suspense fallback={<p>{t("common.loading")}</p>}>
            <BonusCardRequest
                disclaimer={innerHTML}
                formId={FORM_ID}
                onSubmit={onSubmit}
                isLoading={isLoadingCustomer || loading || isLoadingShopNames}
                address={getAddress()}
                customer={{
                    firstName: customer?.firstName,
                    lastName: customer?.lastName,
                    email: customer?.email,
                    dateOfBirth: customer?.dateOfBirth ?? undefined,
                    favouriteStoreId: customer?.favouriteStoreId ?? undefined
                }}
                fallbackStoreId={cart?.shopId}
            />
        </Suspense>
    );
};

export { BonusCardRequestContainer };
